// AHB-Vergleich: aligns the `lines[]` of two AHB documents (old FV vs new FV)
// into an interleaved row list for a side-by-side comparison view.
//
// Alignment is order-preserving via a classic LCS over the per-line identity
// key. Matched keys become `equal`/`modified` rows (old+new set); keys only in
// new are `added` (old null); keys only in old are `removed` (new null).

import type { AhbLine, DiffStatus, DiffRow, AhbDiff } from "@/types/regelwerk";

/** Fields whose change flips a matched line from `equal` to `modified`. */
const MODIFIED_FIELDS = [
  "name",
  "ahb_expression",
  "conditions",
  "section_name",
] as const;

/** Identity key for a line — what we align on. */
function lineKey(l: AhbLine): string {
  return [l.segment_group_key, l.segment_code, l.data_element, l.value_pool_entry].join("|");
}

function isModified(a: AhbLine, b: AhbLine): boolean {
  return MODIFIED_FIELDS.some((f) => a[f] !== b[f]);
}

/**
 * Longest-common-subsequence over two key sequences. Returns the list of
 * matched index pairs `[oldIdx, newIdx]` in ascending order. Positional /
 * duplicate keys are handled naturally by the LCS table.
 */
function lcsKeyPairs(oldKeys: string[], newKeys: string[]): Array<[number, number]> {
  const n = oldKeys.length;
  const m = newKeys.length;

  // dp[i][j] = LCS length of oldKeys[i..] and newKeys[j..]
  const dp: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      dp[i][j] = oldKeys[i] === newKeys[j]
        ? dp[i + 1][j + 1] + 1
        : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const pairs: Array<[number, number]> = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (oldKeys[i] === newKeys[j]) {
      pairs.push([i, j]);
      i++;
      j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      // prefer advancing old (deterministic tie-break)
      i++;
    } else {
      j++;
    }
  }
  return pairs;
}

export function diffAhbLines(oldLines: AhbLine[], newLines: AhbLine[]): AhbDiff {
  const oldKeys = oldLines.map(lineKey);
  const newKeys = newLines.map(lineKey);
  const matches = lcsKeyPairs(oldKeys, newKeys);

  const rows: DiffRow[] = [];
  const counts = { equal: 0, added: 0, removed: 0, modified: 0 };

  const push = (status: DiffStatus, oldLine: AhbLine | null, newLine: AhbLine | null) => {
    rows.push({ status, old: oldLine, new: newLine });
    counts[status]++;
  };

  let oi = 0; // cursor into oldLines
  let ni = 0; // cursor into newLines

  for (const [mo, mn] of matches) {
    // Emit removed lines in old before this match, then added lines in new,
    // then the matched (equal/modified) row. Order: removed then added keeps
    // the old-side context first, matching the side-by-side layout.
    while (oi < mo) push("removed", oldLines[oi++], null);
    while (ni < mn) push("added", null, newLines[ni++]);

    const o = oldLines[oi++];
    const n = newLines[ni++];
    push(isModified(o, n) ? "modified" : "equal", o, n);
  }

  // Tail: anything left over after the last match.
  while (oi < oldLines.length) push("removed", oldLines[oi++], null);
  while (ni < newLines.length) push("added", null, newLines[ni++]);

  return { rows, counts };
}
